import { Badge, Box, Button, Card, Grid, Group, Switch, Tabs, Text } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import queryString from 'query-string';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import AgentCoverageCard from '../components/ChatBotPage/AgentCoverageCard';
import { legacyTabRedirect, resolveSection, resolveTab } from '../components/ChatBotPage/chatBotTabs';
import ConversationsTab from '../components/ChatBotPage/ConversationsTab';
import { conversationIdFromSearch, conversationsUrl } from '../components/ChatBotPage/conversationFormat';
import CustomizeTab from '../components/ChatBotPage/CustomizeTab';
import LearningTab from '../components/ChatBotPage/LearningTab';
import PersonalizationTab from '../components/ChatBotPage/PersonalizationTab';
import StatsHeader from '../components/ChatBotPage/StatsHeader';
import ConnectorsTab from '../components/Connectors/ConnectorsTab';
import KnowledgeBaseTab from '../components/KnowledgeBase/KnowledgeBaseTab';
import SuperAdminSections from '../components/ChatBotPage/superAdmin/SuperAdminSections';
import ErrorMessage from '../components/ErrorMessage';
import Metadata from '../components/Metadata';
import Spinner from '../components/Spinner';
import { Page } from '../constants/pageTitles';
import { daysAgoISODate, dateToEndOfDay, dateToStartOfDay, todayISODate } from '../helpers/dateUtils';
import getErrorMessage from '../helpers/getErrorMessage';
import useFetch from '../helpers/useFetch';
import useIsSuperAdmin from '../helpers/useIsSuperAdmin';
import { chatBotSaveBody } from '../helpers/knowledgeIngest';
import SuperAdminBadge from '../components/SuperAdmin/SuperAdminBadge';
import superAdminAccents from '../components/SuperAdmin/superAdminAccents.module.css';

const defaultChatBot = {
  // The create only brings the record into being — going live stays a deliberate Save.
  enabled: false,
  model: 'claude-sonnet-5-5',
  provider: 'claude',
  profileVersion: '3',
  initialMessages: { default: '' },
  title: { default: '' },
  inputPlaceholder: { default: '' },
  bubbleIcon: null,
  multiLocaleProducts: false,
  documentsNumber: 5,
  productsNumber: 5,
  supportMessage: null,
  supportEmail: null,
  useRelevaTokens: true,
  notificationEnabled: false,
  notificationDelay: 5,
  customThemeEnabled: false,
  customThemeColor: null,
  zIndexEnabled: false,
  zIndex: null,
  position: 'right',
  productDisplayMode: 'list',
  productFeeds: [],
  urlRegex: null,
  urlRegexNoMatch: null,
  orderStatusEnabled: false,
  // Super-admin catalog mapping for the sales agent — [] is "off", see helpers/agentFieldMapping.js.
  agentFieldMapping: [],
  priceDisplayRule: null,
  agentPlaybook: null,
  // Launcher & notification customization, kept in ONE object that is delivered
  // to the widget inside its `theme`. See CustomizeTab.
  appearance: {
    desktop: 'auto', // 'auto' | 'line' | 'bubble'
    mobile: 'auto',
    collapse: false, // show the line's collapse-to-bubble ×
    sendWhenText: false, // notification send button appears only once text is typed
    offsetX: 24,
    offsetY: 24,
  },
};

const ChatBotPage = () => {
  const { accountId, domainId } = useParams();
  const domain = useSelector((state) => state.domain.domain);
  const fetch = useFetch();
  const location = useLocation();
  const navigate = useNavigate();

  const initialPeriodDays = 30;

  const [startDate, setStartDate] = useState(daysAgoISODate(initialPeriodDays - 1));
  const [endDate, setEndDate] = useState(todayISODate());
  const [stats, setStats] = useState();

  const isSuperAdmin = useIsSuperAdmin();

  const [chatBot, setChatBot] = useState();
  const [initialChatBot, setInitialChatBot] = useState();
  const [error, setError] = useState();
  const [savedCount, setSavedCount] = useState(0);
  // The approved brand feeds the widget colors; null when there is none or it cannot be read.
  const [brandContext, setBrandContext] = useState();
  const [learningPending, setLearningPending] = useState(0);

  const initialChatBotStringified = JSON.stringify(initialChatBot);
  const chatBotChanged = initialChatBotStringified !== JSON.stringify(chatBot);

  const parsedSearch = queryString.parse(location.search);
  const tab = resolveTab(parsedSearch.tab, isSuperAdmin);
  const section = resolveSection(parsedSearch.section, chatBot);
  const onConnectors = tab === 'connectors';
  const superAdminRef = useRef(null);
  const conversationId = conversationIdFromSearch(location.search);
  const conversationCount = Number(stats?.uniqueSessions?.[1]?.count || 0);

  // The selected super admin section rides along, so it survives a tab change.
  const setTab = (nextTab) => {
    navigate(
      `${location.pathname}?${queryString.stringify({ tab: nextTab, section: parsedSearch.section }, { sort: false })}`
    );
  };

  const setSection = (nextSection) => {
    navigate(
      `${location.pathname}?${queryString.stringify({ ...parsedSearch, section: nextSection }, { sort: false })}`
    );
  };

  useEffect(() => {
    const redirect = legacyTabRedirect(parsedSearch.tab, isSuperAdmin);
    if (redirect) {
      navigate(`${location.pathname}?${queryString.stringify(redirect, { sort: false })}`, {
        replace: true,
        state: { scrollToSuperAdmin: true },
      });
    }
  }, [parsedSearch.tab, isSuperAdmin, location.pathname, navigate]);

  // The block renders once the chat bot has loaded, so the scroll waits for it.
  useEffect(() => {
    if (location.state?.scrollToSuperAdmin) {
      superAdminRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [location.state, chatBot?.id]);

  const setConversation = (chatSessionId) => {
    navigate(conversationsUrl(location.pathname, chatSessionId, parsedSearch.section));
  };

  const fetchUrl = `/api/admin/accounts/${accountId}/domains/${domainId}`;

  const loadChatBot = useCallback(
    () =>
      fetch(`${fetchUrl}/chatBot`)
        .then(({ chatBot }) => {
          setChatBot(chatBot);
          setInitialChatBot(chatBot);
        })
        .catch(setError),
    [fetchUrl, fetch]
  );

  useEffect(() => {
    loadChatBot();

    const startTs = encodeURIComponent(dateToStartOfDay(startDate, 'local'));
    const endTs = encodeURIComponent(dateToEndOfDay(endDate, 'local'));
    fetch(`${fetchUrl}/chatBot/stats/${startTs}/${endTs}`)
      .then((stats) => {
        setStats(stats);
      })
      .catch(() => setStats(null));
  }, [fetchUrl, fetch, startDate, endDate, loadChatBot]);

  useEffect(() => {
    fetch(`${fetchUrl}/brandContext`)
      .then(({ brandContext }) => setBrandContext(brandContext ?? null))
      .catch(() => setBrandContext(undefined));
  }, [fetchUrl, fetch]);

  useEffect(() => {
    if (!isSuperAdmin || !chatBot?.id) return;
    fetch(`${fetchUrl}/chatBot/learning`)
      .then(({ suggestions }) => setLearningPending(suggestions.filter(({ status }) => status === 'pending').length))
      .catch(() => setLearningPending(0));
  }, [fetchUrl, fetch, isSuperAdmin, chatBot?.id]);

  // Fields already saved by the API (an approved Learning item, a History restore): take them in as loaded,
  // unless the playbook has unsaved edits — those keep their text and the old hash, so their save is refused
  // instead of overwriting the saved change.
  const applySavedChatBot = (fields) => {
    const edited = chatBot.agentPlaybook !== initialChatBot.agentPlaybook;
    const { agentPlaybook: _playbook, agentPlaybookHash: _hash, ...rest } = fields;
    setInitialChatBot((prev) => ({ ...prev, ...fields }));
    setChatBot((prev) => ({ ...prev, ...(edited ? rest : fields) }));
    setSavedCount((count) => count + 1);
  };

  const addChatBot = () => {
    fetch(`${fetchUrl}/chatBot`, {
      method: 'POST',
      body: JSON.stringify(defaultChatBot),
    })
      .then(({ chatBot }) => {
        setChatBot(chatBot);
        setInitialChatBot(chatBot);
      })
      .catch(setError);
  };

  const updateChatBot = (chatBot) => {
    setError(undefined);

    fetch(`${fetchUrl}/chatBot`, {
      method: 'PUT',
      body: JSON.stringify(chatBot),
    })
      .then(({ chatBot }) => {
        setChatBot(chatBot);
        setInitialChatBot(chatBot);
        setSavedCount((count) => count + 1);
      })
      .catch(setError);
  };

  const saveChatBot = () => {
    // Unchanged privileged values (knowledgeIngest, agentFieldMapping, priceDisplayRule,
    // agentPlaybook) go back exactly as loaded (a restatement the API takes from anyone); a changed
    // one is validated and trimmed; an unusable knowledgeIngest is left out.
    const { body, error: invalid } = chatBotSaveBody(chatBot, initialChatBot);
    if (invalid) {
      setError(invalid);
      return;
    }
    updateChatBot(body);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'provider') {
      setChatBot((prev) => ({
        ...prev,
        provider: value,
        model: event.target.model ?? null,
      }));
    } else if (name === 'initialMessages') {
      if (event.target.message !== undefined) {
        setChatBot((prev) => ({
          ...prev,
          initialMessages: { ...prev.initialMessages, [event.target.message]: value },
        }));
      } else {
        setChatBot((prev) => ({
          ...prev,
          initialMessages: value,
        }));
      }
    } else if (name === 'customThemeEnabled') {
      setChatBot((prev) => ({
        ...prev,
        customThemeEnabled: value,
        customThemeColor: value ? '#904dff' : null,
      }));
    } else if (name === 'zIndexEnabled') {
      setChatBot((prev) => ({
        ...prev,
        zIndexEnabled: value,
        zIndex: value ? 99999990 : null,
      }));
    } else if (name === 'profileVersion') {
      // An empty box is not an empty version: null is what pins the store to the
      // live profile pointer.
      setChatBot((prev) => ({
        ...prev,
        profileVersion: value.trim() === '' ? null : value.trim(),
      }));
    } else if (name === 'appearance') {
      // Merge appearance patches functionally so back-to-back updates
      // (before a re-render) don't clobber each other via a stale closure.
      setChatBot((prev) => ({
        ...prev,
        appearance: { ...prev.appearance, ...value },
      }));
    } else if (name === 'urlFilter') {
      if (value === false) {
        setChatBot((prev) => ({
          ...prev,
          urlRegex: null,
          urlRegexNoMatch: null,
        }));
      }
    } else if (name === 'knowledgeIngest') {
      // Super-admin knowledge-import settings, patched one list at a time ({ consentProviders } or
      // { browserSteps }) and merged functionally for the same stale-closure reason as appearance.
      setChatBot((prev) => ({
        ...prev,
        knowledgeIngest: { ...prev.knowledgeIngest, ...value },
      }));
    } else {
      setChatBot((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const getProductFeedId = (feedName) => {
    const feed = chatBot?.productFeeds?.find((f) => f.name === feedName);
    return feed?.productFeedId || null;
  };

  const handleProductFeedChange = (feedName, productFeedId) => {
    const currentFeeds = chatBot?.productFeeds || [];
    const existingIndex = currentFeeds.findIndex((f) => f.name === feedName);

    let newFeeds;
    if (productFeedId === null) {
      newFeeds = currentFeeds.filter((f) => f.name !== feedName);
    } else if (existingIndex >= 0) {
      newFeeds = [...currentFeeds];
      newFeeds[existingIndex] = { name: feedName, productFeedId };
    } else {
      newFeeds = [...currentFeeds, { name: feedName, productFeedId }];
    }

    handleChange({ target: { name: 'productFeeds', value: newFeeds } });
  };

  if (chatBot === undefined) {
    return <Spinner />;
  }

  return (
    <>
      <Metadata title={Page.chatBot} />

      <StatsHeader
        stats={stats}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        currency={domain.currency}
      />

      <Card withBorder mt="sm" mb="sm">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <Group gap="xs">
              <label>Chat Bot</label>
              <SuperAdminBadge
                show={isSuperAdmin && chatBot === null}
                tooltip="Only super admins can enable the chatbot initially"
              />
            </Group>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Switch
              checked={chatBot === null ? false : chatBot?.enabled}
              onChange={(e) => {
                if (chatBot === null) {
                  addChatBot();
                } else {
                  handleChange({ target: { name: 'enabled', value: e.currentTarget.checked } });
                }
              }}
              disabled={chatBot === null ? !isSuperAdmin : false}
            />
          </Grid.Col>
        </Grid>
      </Card>

      {chatBot && chatBot?.id && (
        <Tabs value={tab} onChange={setTab} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="personalization">Personalization</Tabs.Tab>
            <Tabs.Tab
              value="conversations"
              rightSection={
                conversationCount > 0 ? (
                  <Badge size="xs" variant="light" circle={conversationCount < 10}>
                    {conversationCount}
                  </Badge>
                ) : null
              }
            >
              Conversations
            </Tabs.Tab>
            <Tabs.Tab value="knowledgeBase">Knowledge Base</Tabs.Tab>
            <Tabs.Tab value="customize">Customize</Tabs.Tab>
            {isSuperAdmin && (
              <Tabs.Tab
                value="learning"
                rightSection={
                  <Group gap={4} wrap="nowrap">
                    {learningPending > 0 && (
                      <Badge size="xs" variant="light" circle={learningPending < 10}>
                        {learningPending}
                      </Badge>
                    )}
                    <SuperAdminBadge iconOnly show />
                  </Group>
                }
                className={superAdminAccents.tab}
                color="blue"
              >
                Learning
              </Tabs.Tab>
            )}
            {isSuperAdmin && (
              <Tabs.Tab
                value="connectors"
                rightSection={<SuperAdminBadge iconOnly show />}
                className={superAdminAccents.tab}
                color="blue"
              >
                Connectors
              </Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value="personalization">
            <AgentCoverageCard
              fetchUrl={fetchUrl}
              refreshKey={savedCount}
              accountId={accountId}
              domainId={domainId}
              isSuperAdmin={isSuperAdmin}
            />
            <PersonalizationTab chatBot={chatBot} handleChange={handleChange} domainLocale={domain.locale} />
          </Tabs.Panel>

          <Tabs.Panel value="conversations">
            <ConversationsTab
              fetchUrl={fetchUrl}
              accountId={accountId}
              domainId={domainId}
              startDate={startDate}
              endDate={endDate}
              sessionId={conversationId}
              onSessionChange={setConversation}
            />
          </Tabs.Panel>

          <Tabs.Panel value="knowledgeBase">
            <KnowledgeBaseTab isSuperAdmin={isSuperAdmin} />
          </Tabs.Panel>

          <Tabs.Panel value="customize">
            <CustomizeTab
              chatBot={chatBot}
              handleChange={handleChange}
              domainLocale={domain.locale}
              isSuperAdmin={isSuperAdmin}
              brandContext={brandContext}
              brandHref={`#/accounts/${accountId}/domains/${domainId}`}
            />
          </Tabs.Panel>

          {isSuperAdmin && (
            <Tabs.Panel value="learning">
              <LearningTab
                fetchUrl={fetchUrl}
                onPendingCount={setLearningPending}
                onPlaybookApplied={applySavedChatBot}
              />
            </Tabs.Panel>
          )}

          {isSuperAdmin && (
            <Tabs.Panel value="connectors">
              {/* The page Save is hidden here; unsaved chat bot settings keep a way to be saved. */}
              {chatBotChanged && (
                <Group gap={6} mt="sm">
                  <Text size="sm">Unsaved chat bot settings ·</Text>
                  <Button variant="subtle" size="compact-sm" onClick={saveChatBot}>
                    Save
                  </Button>
                  {error && <ErrorMessage message={getErrorMessage(error)} />}
                </Group>
              )}
              <ConnectorsTab accountId={accountId} domainId={domainId} />
            </Tabs.Panel>
          )}
        </Tabs>
      )}

      {isSuperAdmin && chatBot?.id && !onConnectors && (
        <Box ref={superAdminRef}>
          <SuperAdminSections
            section={section}
            onSectionChange={setSection}
            chatBot={chatBot}
            handleChange={handleChange}
            accountId={accountId}
            domainId={domainId}
            domain={domain}
            getProductFeedId={getProductFeedId}
            onProductFeedChange={handleProductFeedChange}
            fetchUrl={fetchUrl}
            chatBotChanged={chatBotChanged}
            refreshKey={savedCount}
            onChatBotRestored={applySavedChatBot}
            onChatBotReload={loadChatBot}
          />
        </Box>
      )}

      {!onConnectors && (
        <Box ta="right" mt="md">
          <Button disabled={!chatBotChanged} onClick={saveChatBot}>
            <IconDeviceFloppy size={16} /> {chatBotChanged ? 'Save' : 'Saved'}
          </Button>
          {error && <ErrorMessage message={getErrorMessage(error)} />}
        </Box>
      )}
    </>
  );
};

export default ChatBotPage;
